import { useState, useRef, useEffect } from "react";
import { Box, Typography, Button, Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { colors } from "../../../theme/palette";

const EntityAbout = ({ text, title, isLoading }: { text: string; title?: string; isLoading?: boolean }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const textElement = textRef.current;
    if (textElement) {
      const { clientHeight, scrollHeight } = textElement;
      if (clientHeight < scrollHeight) {
        setIsClamped(true);
      }
    }
  }, [text]);

  const toggleExpansion = () => {
    setIsExpanded((prev) => !prev);
  };

  if (isLoading) {
    return <EntityAboutSkeleton />;
  }

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: colors.White,
        border: "1px solid #EAECF0",
        borderRadius: "8px",
        overflow: "hidden",
        padding: "20px",
      }}
    >
      {title && (
        <Typography variant="body16Bold" sx={{ marginBottom: "10px", fontWeight: 700 }}>
          {t(`pages.entity.about${title}`)}
        </Typography>
      )}

      {!text ? (
        <Typography variant="body14Regular" sx={{ color: colors.Black100, display: "block" }}>
          {t("pages.entity.noAbout")}
        </Typography>
      ) : (
        <>
          <Typography
            variant="body13Regular"
            sx={{
              color: colors.DarkMode25,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              WebkitLineClamp: isExpanded ? "none" : 4,
            }}
            ref={textRef}
          >
            {text}
          </Typography>
          {isClamped && (
            <Button
              onClick={toggleExpansion}
              variant="text"
              sx={{
                marginTop: "10px",
                color: "#008B07",
                fontFamily: "HeadingNowVarTrial",
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: "24px",
              }}
            >
              {isExpanded ? t("pages.entity.showLess") : t("pages.entity.showMore")}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default EntityAbout;

const EntityAboutSkeleton = () => {
  return (
    <Box
      sx={{
        position: "relative",
        height: "auto",
        backgroundColor: colors.White,
        border: "1px solid #EAECF0",
        borderRadius: "8px",
        overflow: "hidden",
        padding: "20px",
      }}
    >
      {/* Title Skeleton */}
      <Skeleton variant="text" width="30%" height={24} sx={{ marginBottom: "10px" }} />

      {/* Text Content Skeleton */}
      <Box>
        <Skeleton variant="text" height={20} width="100%" />
        <Skeleton variant="text" height={20} width="100%" />
        <Skeleton variant="text" height={20} width="85%" />
      </Box>
    </Box>
  );
};
